import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const apiClient = axios.create({ baseURL: API });

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user, logout } = useAuth();
  const [tables, setTables] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [settings, setSettings] = useState({
    snooker_price_per_hour: 300,
    pool_price_per_hour: 150,
    table_rates: {
      "Table 1": 300,
      "Table 2": 300,
      "Table 3": 250,
      "Table 4": 200,
      "Table 5": 200,
      "Table 6": 150,
      "Table 7": 150,
    },
    playstation_rates: {
      "PlayStation 1": { "1": 120, "2": 120, "3": 150, "4": 200 },
      "PlayStation 2": { "1": 120, "2": 120, "3": 150, "4": 200 },
      "PlayStation 3": { "1": 120, "2": 120, "3": 150, "4": 200 },
    },
  });
  const [loading, setLoading] = useState(true);
  const sessionExpiredRef = useRef(false);

  useEffect(() => {
    if (user?.token) {
      apiClient.defaults.headers.common.Authorization = `Bearer ${user.token}`;
    } else {
      delete apiClient.defaults.headers.common.Authorization;
    }
    sessionExpiredRef.current = false;
  }, [user]);

  // Sign out on expired/invalid tokens (401). 403 is a role mismatch, not expiry.
  useEffect(() => {
    const interceptorId = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && user && !sessionExpiredRef.current) {
          sessionExpiredRef.current = true;
          logout();
          toast.error("Session expired — please sign in again");
        }
        return Promise.reject(error);
      },
    );
    return () => apiClient.interceptors.response.eject(interceptorId);
  }, [user, logout]);

  const fetchTables = useCallback(async () => {
    if (!user?.token) return;
    try {
      const res = await apiClient.get("/tables");
      setTables(res.data);
    } catch (e) {
      console.error("Failed to fetch tables:", e);
    }
  }, [user?.token]);

  // Who is waiting for a table and what they've bought. Returns null on failure,
  // so a caller can carry on with the list it already has.
  const fetchWaiting = useCallback(async () => {
    if (!user?.token) return null;
    try {
      const res = await apiClient.get("/waiting");
      setWaiting(res.data.tabs);
      return res.data.tabs;
    } catch (e) {
      console.error("Failed to fetch the waiting list:", e);
      return null;
    }
  }, [user?.token]);

  const fetchSettings = useCallback(async () => {
    if (!user?.token) return;
    try {
      const res = await apiClient.get("/settings");
      // Keep the same reference when nothing changed so polling doesn't reset
      // consumers (e.g. the Settings form) every cycle
      setSettings((prev) => (JSON.stringify(prev) === JSON.stringify(res.data) ? prev : res.data));
    } catch (e) {
      console.error("Failed to fetch settings:", e);
    }
  }, [user?.token]);

  const fetchDashboardStats = useCallback(async (params = {}) => {
    if (!user?.token) return null;
    try {
      const res = await apiClient.get("/dashboard/stats", { params });
      return res.data;
    } catch (e) {
      console.error("Failed to fetch dashboard stats:", e);
      return null;
    }
  }, [user?.token]);

  const startSession = useCallback(async (tableId, payload = {}) => {
    try {
      const res = await apiClient.post(`/tables/${tableId}/start`, payload);
      await fetchTables();
      return res.data;
    } finally {
      // Gone to this game, or to another device if the start was refused.
      if (payload.waiting_tab_id) await fetchWaiting();
    }
  }, [fetchTables, fetchWaiting]);

  const stopSession = useCallback(async (tableId, dues = []) => {
    const res = await apiClient.post(`/tables/${tableId}/stop`, dues.length ? { dues } : undefined);
    await fetchTables();
    return res.data;
  }, [fetchTables]);

  // A game started by mistake: no bill, no history row. Anything already bought
  // at the table stays recorded as a cafe sale, and anyone who came off the
  // waiting list goes back on it.
  const cancelSession = useCallback(async (tableId) => {
    const res = await apiClient.post(`/tables/${tableId}/cancel`);
    await Promise.all([fetchTables(), fetchWaiting()]);
    return res.data;
  }, [fetchTables, fetchWaiting]);

  // Apply a change to one table's running session in local state. Pause/resume
  // and tab edits return exactly what changed, so there's no need to refetch
  // every table after each tap.
  const patchActiveSession = useCallback((tableId, patch) => {
    setTables((prev) => prev.map((t) => (
      t.id === tableId && t.active_session
        ? { ...t, active_session: patch(t.active_session) }
        : t
    )));
  }, []);

  // On failure the table may have changed elsewhere (e.g. stopped on another
  // device) — resync before surfacing the error.
  const withResync = useCallback(async (fn) => {
    try {
      return await fn();
    } catch (e) {
      await fetchTables();
      throw e;
    }
  }, [fetchTables]);

  const pauseSession = useCallback((tableId) => withResync(async () => {
    const res = await apiClient.post(`/tables/${tableId}/pause`);
    patchActiveSession(tableId, (s) => ({ ...res.data, orders: s.orders || [] }));
    return res.data;
  }), [withResync, patchActiveSession]);

  const resumeSession = useCallback((tableId) => withResync(async () => {
    const res = await apiClient.post(`/tables/${tableId}/resume`);
    patchActiveSession(tableId, (s) => ({ ...res.data, orders: s.orders || [] }));
    return res.data;
  }), [withResync, patchActiveSession]);

  // Loser pays: one frame against the player who lost it, and an undo for a
  // mis-tap. Both return the session, so the card re-reads its own tally.
  const recordFrame = useCallback((tableId, loserSideId) => withResync(async () => {
    const res = await apiClient.post(`/tables/${tableId}/frames`, { loser_side_id: loserSideId });
    patchActiveSession(tableId, (prev) => ({ ...res.data, orders: prev.orders || [] }));
    return res.data;
  }), [withResync, patchActiveSession]);

  const removeFrame = useCallback((tableId, frameId) => withResync(async () => {
    const res = await apiClient.delete(`/tables/${tableId}/frames/${frameId}`);
    patchActiveSession(tableId, (prev) => ({ ...res.data, orders: prev.orders || [] }));
    return res.data;
  }), [withResync, patchActiveSession]);

  // Players change tables mid-game. The game moves with them: clock, tab and bill
  // all carry over, so there is no first table left to collect from.
  const moveGame = useCallback((tableId, toTableId) => withResync(async () => {
    const res = await apiClient.post(`/tables/${tableId}/move`, { to_table_id: toTableId });
    await fetchTables();
    return res.data;
  }), [withResync, fetchTables]);

  // Players swap pads mid-game. The clock keeps running: time already played
  // stays at the old rate, everything after is charged at the new one.
  const setControllers = useCallback((tableId, count) => withResync(async () => {
    const res = await apiClient.post(`/tables/${tableId}/controllers`, { controller_count: count });
    setTables((prev) => prev.map((t) => (
      t.id === tableId
        ? { ...t, controller_count: count, active_session: { ...res.data, orders: t.active_session?.orders || [] } }
        : t
    )));
    return res.data;
  }), [withResync]);

  // One tap = one unit on the table's tab, settled with the game on stop.
  // A bulk add lands as a single line of that quantity.
  const addToTab = useCallback((tableId, itemId, quantity = 1, playerId = null) => withResync(async () => {
    const res = await apiClient.post("/cafe/orders", {
      item_id: itemId, quantity, table_id: tableId, player_id: playerId,
    });
    const o = res.data;
    const tabEntry = {
      id: o.id, item_id: o.item_id, item_name: o.item_name, source: o.source,
      quantity: o.quantity, unit_price: o.unit_price, total_amount: o.total_amount,
      created_at: o.created_at, player_id: o.player_id, player_name: o.player_name,
    };
    patchActiveSession(tableId, (s) => ({ ...s, orders: [...(s.orders || []), tabEntry] }));
    return tabEntry;
  }), [withResync, patchActiveSession]);

  const removeFromTab = useCallback((tableId, orderId) => withResync(async () => {
    await apiClient.delete(`/tables/${tableId}/orders/${orderId}`);
    patchActiveSession(tableId, (s) => ({ ...s, orders: (s.orders || []).filter((o) => o.id !== orderId) }));
  }), [withResync, patchActiveSession]);

  // Puts an item already on the tab onto someone's name (or back on the table).
  const moveTabItem = useCallback((tableId, orderId, moveTo) => withResync(async () => {
    const res = await apiClient.patch(`/tables/${tableId}/orders/${orderId}`, { move_to: moveTo });
    const { removed, ...entry } = res.data;
    patchActiveSession(tableId, (prev) => ({
      ...prev,
      orders: (prev.orders || []).map((o) => (o.id === orderId ? { ...o, ...entry } : o)),
    }));
    return res.data;
  }), [withResync, patchActiveSession]);

  // Re-counts one line on the tab — how "−" takes a single unit off a bulk
  // line instead of the whole thing. 0 removes the line; the server keeps the
  // unit price the line was added at.
  const setTabQuantity = useCallback((tableId, orderId, quantity) => withResync(async () => {
    const res = await apiClient.patch(`/tables/${tableId}/orders/${orderId}`, { quantity });
    const { removed, ...entry } = res.data;
    patchActiveSession(tableId, (s) => ({
      ...s,
      orders: removed
        ? (s.orders || []).filter((o) => o.id !== orderId)
        : (s.orders || []).map((o) => (o.id === orderId ? { ...o, ...entry } : o)),
    }));
    return res.data;
  }), [withResync, patchActiveSession]);

  // A name may have gone onto a table on another device, or that table's game
  // ended — resync both lists before surfacing the error.
  const withWaitingResync = useCallback(async (fn) => {
    try {
      return await fn();
    } catch (e) {
      await Promise.all([fetchTables(), fetchWaiting()]);
      throw e;
    }
  }, [fetchTables, fetchWaiting]);

  const patchWaitingOrders = useCallback((tabId, patch) => {
    setWaiting((prev) => prev.map((t) => (
      t.id === tabId ? { ...t, orders: patch(t.orders) } : t
    )));
  }, []);

  const addWaiting = useCallback(async (name) => {
    const res = await apiClient.post("/waiting", { name });
    setWaiting((prev) => [...prev, res.data]);
    return res.data;
  }, []);

  const addToWaiting = useCallback((tabId, itemId, quantity = 1) => withWaitingResync(async () => {
    const res = await apiClient.post("/cafe/orders", { item_id: itemId, quantity, waiting_id: tabId });
    const o = res.data;
    const entry = {
      id: o.id, item_id: o.item_id, item_name: o.item_name, source: o.source,
      quantity: o.quantity, unit_price: o.unit_price, total_amount: o.total_amount,
      created_at: o.created_at, waiting_name: o.waiting_name,
    };
    patchWaitingOrders(tabId, (orders) => [...orders, entry]);
    return entry;
  }), [withWaitingResync, patchWaitingOrders]);

  // Re-counts one line, as on a table's tab: 0 takes it off and restocks it.
  const setWaitingQuantity = useCallback((tabId, orderId, quantity) => withWaitingResync(async () => {
    const res = await apiClient.patch(`/waiting/${tabId}/orders/${orderId}`, { quantity });
    const { removed, ...entry } = res.data;
    patchWaitingOrders(tabId, (orders) => (
      removed
        ? orders.filter((o) => o.id !== orderId)
        : orders.map((o) => (o.id === orderId ? { ...o, ...entry } : o))
    ));
    return res.data;
  }), [withWaitingResync, patchWaitingOrders]);

  // Puts what someone bought onto a game that is already running.
  const seatWaiting = useCallback((tabId, tableId) => withWaitingResync(async () => {
    await apiClient.post(`/waiting/${tabId}/seat`, { table_id: tableId });
    setWaiting((prev) => prev.filter((t) => t.id !== tabId));
    await fetchTables();
  }), [withWaitingResync, fetchTables]);

  // Paid at the counter and not playing: the items stay as ordinary sales.
  const closeWaiting = useCallback((tabId) => withWaitingResync(async () => {
    await apiClient.post(`/waiting/${tabId}/close`);
    setWaiting((prev) => prev.filter((t) => t.id !== tabId));
  }), [withWaitingResync]);

  // Renames the display name only; rates stay attached to the table.
  const renameTable = useCallback(async (tableId, name) => {
    const res = await apiClient.put(`/tables/${tableId}`, { name });
    setTables((prev) => prev.map((t) => (
      t.id === tableId
        ? {
            ...t,
            ...res.data,
            // a game in progress is renamed with the table
            active_session: t.active_session
              ? { ...t.active_session, table_name: res.data.name }
              : t.active_session,
          }
        : t
    )));
    return res.data;
  }, []);

  const updateSettings = useCallback(async (newSettings) => {
    const res = await apiClient.put("/settings", newSettings);
    setSettings(res.data);
    return res.data;
  }, []);

  // Initial load only — fetched once per page load. Table/settings state is then
  // kept fresh by the actions that change it (start/stop session, save settings),
  // not by background polling.
  useEffect(() => {
    const init = async () => {
      if (!user?.token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      await Promise.all([fetchTables(), fetchSettings(), fetchWaiting()]);
      setLoading(false);
    };
    init();
  }, [fetchTables, fetchSettings, fetchWaiting, user?.token]);

  return (
    <AppContext.Provider value={{
      tables, settings, loading,
      fetchTables, fetchSettings,
      startSession, stopSession, cancelSession, updateSettings,
      pauseSession, resumeSession, setControllers, moveGame, recordFrame, removeFrame,
      addToTab, removeFromTab, setTabQuantity, moveTabItem, renameTable,
      waiting, fetchWaiting, addWaiting, addToWaiting, setWaitingQuantity, seatWaiting, closeWaiting,
      fetchDashboardStats,
      apiClient,
      API,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be inside AppProvider");
  return ctx;
}
